import { bbox, centroid, featureCollection, point } from "@turf/turf";
import { useCallback, useEffect, useState, RefObject } from "react";
import { MapRef } from "react-map-gl/mapbox";
import { incidentMarkerMinimumPixelSize } from "../styles";
import {
  IncidentMarkerCollection,
  UseIncidentMarkersParams,
} from "../../types";
import { incidentMarker } from "../incidentMarker";
import { svgToMapImage } from "../svgToMapImage";

const markerImageId = "incident-marker";

export function useIncidentMarkers(
  mapRef: RefObject<MapRef | null>,
  { visibleIncidentCollection }: UseIncidentMarkersParams,
) {
  const [incidentMarkerCollection, setIncidentMarkerCollection] =
    useState<IncidentMarkerCollection>(featureCollection([]));

  const updateIncidentMarkerCollection = useCallback(() => {
    const map = mapRef.current;

    if (!map || !visibleIncidentCollection?.features?.length) {
      setIncidentMarkerCollection(featureCollection([]));
      return;
    }

    const markerFeatures: IncidentMarkerCollection["features"] =
      visibleIncidentCollection.features.flatMap(
        (feature, incidentFeatureIndex) => {
          const featureBounds = bbox(feature);
          const southWest = map.project([featureBounds[0], featureBounds[1]]);
          const northEast = map.project([featureBounds[2], featureBounds[3]]);

          const width = Math.abs(northEast.x - southWest.x);
          const height = Math.abs(southWest.y - northEast.y);

          if (
            width >= incidentMarkerMinimumPixelSize &&
            height >= incidentMarkerMinimumPixelSize
          )
            return [];

          return [
            point(centroid(feature).geometry.coordinates, {
              ...feature.properties,
              incidentFeatureIndex,
            }),
          ];
        },
      );

    setIncidentMarkerCollection(featureCollection(markerFeatures));
  }, [mapRef, visibleIncidentCollection]);

  const handleIncidentMarkerLoad = useCallback(async () => {
    const map = mapRef.current;

    if (!map) return;

    if (!map.hasImage(markerImageId)) {
      const image = await svgToMapImage(incidentMarker);

      map.addImage(markerImageId, image, {
        sdf: true,
      });
    }

    updateIncidentMarkerCollection();
  }, [mapRef, updateIncidentMarkerCollection]);

  useEffect(() => {
    updateIncidentMarkerCollection();
  }, [updateIncidentMarkerCollection]);

  return {
    incidentMarkerCollection,
    markerImageId,
    handleIncidentMarkerLoad,
    updateIncidentMarkerCollection,
  };
}
